import React from 'react';
import { Link } from 'react-router-dom';
import ScanResults from '../components/ScanResults';
import LoadingOverlay from '../components/LoadingOverlay';
import NewsCarousel from '../components/NewsCarousel';
import { idbSetWithExpiry, idbGetWithExpiry } from '../storage/expiredstorage';

// Cache expiration time in milliseconds (3 hours)
const CACHE_EXPIRY_TIME_MS = 10800000;

// Domain validation regex
const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

const Home = () => {
  const [domain, setDomain] = React.useState('');
  const [isValidDomain, setIsValidDomain] = React.useState(true);
  const [isDirty, setIsDirty] = React.useState(false);
  const [scanResults, setScanResults] = React.useState(null);
  const [statusCode, setStatusCode] = React.useState(null);
  const [errorInfo, setErrorInfo] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [headers, setHeaders] = React.useState({});

  // News items for the carousel
  const newsItems = [
    {
      title: "Quantum Decryption of RSA Is Much Closer Than Expected",
      source: "SecurityWeek (March 2026)",
      url: "https://www.securityweek.com/quantum-decryption-of-rsa-is-much-closer-than-expected/"
    },
    {
      title: "Mogelijk onheil door kwantumcomputer dringt niet genoeg door bij overheid",
      source: "NU.nl (February 2026)",
      url: "https://www.nu.nl/tech/6384878/mogelijk-onheil-door-kwantumcomputer-dringt-niet-genoeg-door-bij-overheid.html"
    },
    { 
      title: "Cryptocalypse: EU demands quantum-safe encryption partly by 2030",
      source: "Heise (June 2025)",
      url: "https://www.heise.de/en/news/Cryptocalypse-EU-demands-quantum-safe-encryption-partly-by-2030-10456642.html"
    },
    { 
      title: "Google Research Slashes Estimated Resources to Break RSA Encryption",
      source: "IoT World Today (May 2025)",
      url: "https://www.iotworldtoday.com/quantum/google-research-slashes-estimated-resources-to-break-rsa-encryption"
    },
    { 
      title: "Tijdbom onder encryptie: hoe quantumcomputers onze data bedreigen",
      source: "Computable (18 April 2025)",
      url: "https://www.computable.nl/2025/04/18/tijdbom-onder-encryptie-hoe-quantumcomputers-onze-data-bedreigen/"
    },
    { 
      title: "The Quantum Apocalypse Is Coming. Be Very Afraid",
      source: "Wired (24 March 2025)",
      url: "https://www.wired.com/story/q-day-apocalypse-quantum-computers-encryption/?utm_source=chatgpt.com"
    },
    { 
      title: "UK Cybersecurity Agency Warns Over Risk of Quantum Hackers",
      source: "The Guardian (20 March 2025)",
      url: "https://www.theguardian.com/technology/2025/mar/20/uk-cybersecurity-agency-quantum-hackers?utm_source=chatgpt.com"
    },
    { 
      title: "The Government Unveils Its Quantum Counter-Weapons",
      source: "Politico (13 August 2024)",
      url: "https://www.politico.com/newsletters/digital-future-daily/2024/08/13/the-government-unveils-its-quantum-counter-weapons-00173832?utm_source=chatgpt.com"
    },
    { 
      title: "U.S. and China Race to Shield Secrets From Quantum Computers",
      source: "Reuters (14 December 2023)",
      url: "https://www.reuters.com/investigates/special-report/us-china-tech-quantum/?utm_source=chatgpt.com"
    },
    { 
      title: "Will These Algorithms Save You From Quantum Threats?",
      source: "Wired (8 July 2022)",
      url: "https://www.wired.com/story/quantum-proof-encryption-is-here-but-theres-a-catch/?utm_source=chatgpt.com"
    },
    { 
      title: "CEOs Who Aren't Yet Preparing for the Quantum Revolution Are 'Already Too Late,' IBM Exec Says",
      source: "Business Insider (27 May 2025)",
      url: "https://www.businessinsider.com/future-proofing-technology-systems-executives-prepare-quantum-revolution-2025-5?utm_source=chatgpt.com"
    },
    { 
      title: "Apple's iMessage Is Getting Post-Quantum Encryption",
      source: "Wired (February 2024)",
      url: "https://www.wired.com/story/apple-pq3-post-quantum-encryption?utm_source=chatgpt.com"
    }
  ];

  // Generic function to fetch a specific header
  const fetchHeader = async (headerName, infoDisplayText, warningDisplayText, infoComponent, headerTitle) => {
    try {
      // Check if we have a cached header value
      const cachedHeader = await idbGetWithExpiry(`header_${headerName}`);

      if (cachedHeader) {
        // Use the cached header value
        processHeaderValue(headerName, cachedHeader, infoDisplayText, warningDisplayText, infoComponent, headerTitle);
        return;
      }

      // If no cached value, make the fetch request
      const response = await fetch('/health');

      // Get the specified header
      const headerValue = response.headers.get(headerName);

      // Cache the header value with 1-minute expiration (60000 milliseconds)
      await idbSetWithExpiry(`header_${headerName}`, headerValue, CACHE_EXPIRY_TIME_MS);

      // Process the header value
      processHeaderValue(headerName, headerValue, infoDisplayText, warningDisplayText, infoComponent, headerTitle);
    } catch (error) {
      console.error(`Error fetching ${headerName} header:`, error);
    }
  };

  // Helper function to process the header value and update state
  const processHeaderValue = (headerName, headerValue, infoDisplayText, warningDisplayText, infoComponent, headerTitle) => {
    // Determine if we should show info or warning message
    const isInfoMessage = headerValue && headerValue.includes(infoComponent);

    // Select the appropriate display text based on the condition
    const displayText = isInfoMessage ? infoDisplayText : warningDisplayText;

    // Replace the placeholder in displayText with the actual header value
    const formattedDisplayText = displayText.replace('@headerValue', headerValue);

    // Create the contact message to append to the display text
    const contactMessage = (
      <React.Fragment>
        {" "}Want a similar check on your website? <Link to="/contact-us" className="text-blue-600 hover:underline">Contact us</Link> to learn more about our quantum-safe solutions.
      </React.Fragment>
    );

    // Add styling information based on message type
    const messageType = isInfoMessage ? 'info' : 'warning';
    const icon = isInfoMessage ? '🎉' : '⚠️';
    const styling = isInfoMessage 
      ? 'bg-green-100 border border-green-400' 
      : 'bg-yellow-100 border border-yellow-400';

    // Update state with the header value, formatted display text, styling, and title
    setHeaders(prevHeaders => ({
      ...prevHeaders,
      [headerName]: { 
        value: headerValue, 
        displayText: formattedDisplayText,
        contactMessage,
        messageType,
        icon,
        styling,
        headerTitle: headerTitle || `${headerName.charAt(0).toUpperCase() + headerName.slice(1)} Header`
      }
    }));
  };

  // Validate domain using regex
  const validateDomain = (value) => {
    return domainRegex.test(value);
  };

  // Handle domain input changes
  const handleDomainChange = (e) => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trim(); // Remove leading and trailing spaces

    // Only update the displayed value if it's different from the raw input
    // This prevents the cursor from jumping to the end when typing in the middle
    if (rawValue !== trimmedValue) {
      setDomain(trimmedValue);
    } else {
      setDomain(rawValue);
    }

    setIsDirty(true);

    // Only show validation error if there's input
    if (trimmedValue) {
      setIsValidDomain(validateDomain(trimmedValue));
    } else {
      setIsValidDomain(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim, lowercase and validate before submission
    const trimmedDomain = domain.trim().toLowerCase();

    if (!validateDomain(trimmedDomain)) {
      setIsValidDomain(false);
      return; // Prevent form submission
    }

    // Update the domain state with the trimmed value
    setDomain(trimmedDomain);

    // Set loading state to true before API call or cache retrieval
    setIsLoading(true);

    try {
      // Check for cached results first
      const cachedResults = await idbGetWithExpiry(trimmedDomain);

      if (cachedResults) {
        // Use cached results instead of making a new API call
        setScanResults(cachedResults.responseData || {});
        setStatusCode(cachedResults.statusCode);

        if (cachedResults.error) {
          setErrorInfo(cachedResults.errorMessage);
        } else {
          setErrorInfo(null);
        }

        console.log('Using cached results for:', trimmedDomain);
        return; // Exit early since we have cached results
      }

      // If no cache, proceed with API call
      const response = await fetch('/api/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: trimmedDomain }),
      });

      // No need to get the date header here as it's already fetched in fetchHeadersOnly

      const data = await response.json();

      if (data.success) {
        // Store the response data and status information
        setScanResults(data.responseData || {});
        setStatusCode(data.statusCode);

        // Cache successful results
        await idbSetWithExpiry(trimmedDomain, data, CACHE_EXPIRY_TIME_MS);

        // If there was an error during the GET request
        if (data.error) {
          setErrorInfo(data.errorMessage);
        } else {
          setErrorInfo(null);
        }
      } else {
        alert('Error: Failed to submit domain for scanning.');
      }
    } catch (error) {
      console.error('Error submitting domain:', error);
      alert('Error: Could not connect to the server. Please try again later.');
    } finally {
      // Set loading state to false after API call completes (success or error)
      setIsLoading(false);
    }
  };

  // Reset the results to go back to the form
  const handleBack = () => {
    // Reset scan-related state variables but preserve headers
    setScanResults(null);
    setStatusCode(null);
    setErrorInfo(null);
    // We don't reset headers to preserve the browser check message
  };

  // Fetch headers when component mounts
  React.useEffect(() => {
    // Fetch the date header by default
    fetchHeader(
      'x-key-exchange-group',
      'The negotiated key-exchange group is @headerValue, which demonstrates that your browser is quantum-safe.',
      'The negotiated key-exchange group is @headerValue, which demonstrates that your browser is not yet quantum-safe.',
      'MLKEM',
      'Quantum Safety Browser Check'
    );

    // You can fetch additional headers here if needed
    // For example:
    // fetchHeader('content-type', 'Content Type: @headerValue is supported', 'Content Type: @headerValue might not be fully supported', 'application/json', 'Content Type Information');
    // fetchHeader('server', 'Server: @headerValue is recommended', 'Server: @headerValue might have known issues', 'nginx', 'Server Information');
  }, []);

  // If we have scan results, show the results page
  if (scanResults) {
    return (
      <ScanResults 
        results={scanResults} 
        statusCode={statusCode} 
        errorInfo={errorInfo} 
        onBack={handleBack} 
      />
    );
  }

  // Otherwise show the scan form
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Quantum Website Scanner</h1>
      <h2 className="text-xl font-semibold mb-6 bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent drop-shadow-sm animate-pulse">Stay Ahead of Quantum Threats</h2>
      <p className="mb-6">
        Attackers can copy your encrypted data today and break it tomorrow with quantum computers – the store-now, decrypt-later threat. In a short time we reveal which certificates, protocols and key stores keep that door open. We turn the results into a step-by-step action plan and—if you choose—our own specialists apply every fix, so you don't need extra engineers. The outcome: future-proof encryption and demonstrable compliance with NIS 2 and DORA.
      </p>
      <p className="mb-6">
         <strong>Test your website for Post-Quantum Cryptography readiness. Enter your domain below to start a scan.</strong>
      </p>
      <form onSubmit={handleSubmit} className="max-w-md">
        {/* Flex container for responsive layout */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-grow">
            <label htmlFor="domain" className="block text-gray-700 mb-2">Domain Name</label>
            <div className="flex flex-col">
              <input
                type="text"
                id="domain"
                value={domain}
                onChange={handleDomainChange}
                placeholder="example.com"
                className={`w-full px-3 py-2 border ${!isValidDomain && isDirty ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                required
              />
            </div>
            {/* Error message container with conditional height */}
            <div className={!isValidDomain && isDirty ? "min-h-[160px]" : ""}>
              {!isValidDomain && isDirty && (
                <div className="text-red-500 text-sm mt-1">
                  <p>Please enter a valid domain name. A valid domain:</p>
                  <ul className="list-disc ml-5 mt-1">
                    <li>Must contain at least one dot (e.g., example.com)</li>
                    <li>Can include subdomains (e.g., sub.example.com)</li>
                    <li>Must start and end with a letter or number</li>
                    <li>Can contain letters, numbers, and hyphens</li>
                    <li>Cannot have hyphens at the start or end of a segment</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Button aligned to input field height on md+ screens */}
          <div className="md:self-start md:pt-8 mt-2 md:mt-0">
            <button
              type="submit"
              className={`${!domain || (isDirty && !isValidDomain) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center transition-colors duration-200 w-full md:w-[140px] min-w-[140px]`}
              disabled={!domain || (isDirty && !isValidDomain) || isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Quantum Scan'
              )}
            </button>
          </div>
        </div>
      </form>


      {/* Dynamic headers display */}
      {Object.keys(headers).length > 0 && (
        <div className="mt-8">
          {Object.entries(headers).map(([headerName, { displayText, contactMessage, icon, styling, headerTitle }]) => (
            <div key={headerName} className={`p-4 mb-4 rounded-md ${styling}`}>
              <h2 className="text-xl font-bold mb-2">
                {icon} {headerTitle}
              </h2>
              <p className="text-lg">
                {displayText}
                {contactMessage}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* News carousel below browser check */}
      <div className="mt-8">
        <NewsCarousel 
          items={newsItems} 
          autoScrollInterval={6000} 
          pauseOnHover={true} 
        />
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay isVisible={isLoading} />
    </div>
  );
};

export default Home;
